/**
 * Phase 3 vocabulary for the manager's reservation screens (#642):
 * deposits and their settlement, inspections, required documents, the
 * waitlist, access codes, no-show suspensions, pricing rules and the
 * revenue report.
 *
 * Money stays a decimal string end to end; anything that adds or
 * compares goes through cents (`moneyToCents`), never a float.
 *
 * Pure functions only, so the rules are unit-testable.
 */
import {
  AmenityAccessCodeMode,
  AmenityDocumentKind,
  AmenityPriceRuleFeeType,
  AmenitySuspensionReturn,
  ReservationChecklistItem,
  ReservationDepositStatus,
  ReservationDocumentStatus,
  ReservationFeeLine,
  ReservationFeeLineKind,
  ReservationInspectionKind,
  ReservationInspectionOutcome,
  ReservationReturn,
  ReservationRevenueMonth,
  ReservationWaitlistStatus,
} from '../clients/rust-api-client';
import { centsToMoney, formatMoney, moneyToCents } from '../utils/billingHelpers';
import { WEEKDAYS_SHORT, formatMinute, isValidFee } from './reservationMeta';

// ---------------------------------------------------------------------------
// Deposits
// ---------------------------------------------------------------------------

export const DEPOSIT_STATUS_LABELS: Record<ReservationDepositStatus, string> = {
  none: 'No deposit',
  held: 'Held',
  released: 'Returned in full',
  partially_released: 'Partly kept',
  forfeited: 'Kept in full',
  refund_due: 'Refund due — already paid',
  credited: 'Returned — credited to the account',
  refunded: 'Returned — refunded by cheque',
};

export const depositStatusLabel = (status: string): string =>
  DEPOSIT_STATUS_LABELS[status as ReservationDepositStatus] ?? status;

/**
 * What became of paid money owed back (#703), for the fee or the deposit:
 * returned as a credit, returned and refunded by cheque, or — on a booking
 * from before returns were automatic — still waiting for a person. Null
 * for any other status.
 */
export const returnedText = (what: 'fee' | 'deposit', status: string, amount: string): string | null => {
  const money = formatMoney(amount);
  switch (status) {
    case 'credited':
      return `The ${what} had already been paid, so ${money} was returned to the home’s account as a credit against future charges.`;
    case 'refunded':
      return `The ${what} had already been paid, so ${money} was returned and recorded on the home’s ledger as refunded by cheque.`;
    case 'refund_due':
      return `The ${what} had already been paid before returns were automatic, so ${money} is still owed back. Check the home’s ledger, then return it to the account.`;
    default:
      return null;
  }
};

/** What a settled or cancelled deposit gave back: the deposit less anything kept. */
export const depositReturned = (r: Pick<ReservationReturn, 'deposit_amount' | 'deposit_deduction_amount'>): string =>
  centsToMoney(moneyToCents(r.deposit_amount) - moneyToCents(r.deposit_deduction_amount ?? '0'));

/** What settling a deposit with `release` returned does: the three rows of the design's table. */
export type SettleKind = 'release_all' | 'keep_some' | 'keep_all';

export interface SettlePlan {
  /** The form may be submitted. */
  valid: boolean;
  /** Why not, in words, when the amount itself is wrong. */
  problem: string | null;
  kind: SettleKind | null;
  /** Canonical decimal strings. */
  release: string;
  kept: string;
  /** Keeping anything needs a reason, which the household is shown. */
  needsReason: boolean;
}

/**
 * Settling `deposit` by returning `release` and keeping the rest. All in
 * cents: "$250.00 deposit, return $175.50" keeps exactly $74.50.
 */
export const settlePlan = (deposit: string, release: string, reason: string): SettlePlan => {
  const empty: SettlePlan = {
    valid: false,
    problem: null,
    kind: null,
    release: '0.00',
    kept: '0.00',
    needsReason: false,
  };
  const typed = release.trim();
  if (typed === '') return empty;
  if (!isValidFee(typed)) return { ...empty, problem: 'Enter an amount like 175.00.' };
  const depositCents = moneyToCents(deposit);
  const releaseCents = moneyToCents(typed);
  if (releaseCents > depositCents) {
    return { ...empty, problem: `That is more than the ${formatMoney(deposit)} deposit.` };
  }
  const keptCents = depositCents - releaseCents;
  const kind: SettleKind =
    keptCents === BigInt(0) ? 'release_all' : releaseCents === BigInt(0) ? 'keep_all' : 'keep_some';
  const needsReason = keptCents > BigInt(0);
  return {
    valid: !needsReason || reason.trim().length > 0,
    problem: null,
    kind,
    release: centsToMoney(releaseCents),
    kept: centsToMoney(keptCents),
    needsReason,
  };
};

export const SETTLE_KIND_TEXT: Record<SettleKind, string> = {
  release_all: 'The whole deposit goes back to the household.',
  keep_some: 'Part of the deposit is kept as amenity income; the rest goes back.',
  keep_all: 'The whole deposit is kept as amenity income.',
};

/**
 * Whether a manager can settle the booking's deposit now: one is held and
 * the booking is over. A cancelled booking's deposit is released by the
 * cancellation itself.
 */
export const canSettleDeposit = (
  r: { deposit_status: string; ends_at: number; status: string },
  nowSeconds: number
): boolean =>
  r.deposit_status === 'held' &&
  r.ends_at <= nowSeconds &&
  ['approved', 'completed', 'no_show'].includes(r.status);

// ---------------------------------------------------------------------------
// Fee breakdown
// ---------------------------------------------------------------------------

export const FEE_LINE_KIND_LABELS: Record<ReservationFeeLineKind, string> = {
  base: 'Base fee',
  price_rule: 'Pricing rule',
  guests: 'Guests',
};

/** The lines summed exactly; '0.00' for none. */
export const feeLinesTotal = (lines: ReservationFeeLine[]): string =>
  centsToMoney(lines.reduce((acc, l) => acc + moneyToCents(l.amount), BigInt(0)));

/** Worth showing the lines: more than one, or a single line that is not the plain base fee. */
export const showFeeBreakdown = (lines: ReservationFeeLine[] | null | undefined): boolean =>
  !!lines && (lines.length > 1 || (lines.length === 1 && lines[0].kind !== 'base'));

// ---------------------------------------------------------------------------
// Pricing rules
// ---------------------------------------------------------------------------

const WEEKENDS = [0, 6];
const WEEKDAYS_MON_FRI = [1, 2, 3, 4, 5];
const same = (a: number[], b: number[]) => a.length === b.length && a.every((v, i) => v === b[i]);

/** Weekday numbers (0 = Sunday) in order, without repeats. */
export const normalizeWeekdays = (days: number[]): number[] =>
  Array.from(new Set(days.filter((d) => d >= 0 && d <= 6))).sort((a, b) => a - b);

/** Add or remove a weekday from a chip row. */
export const toggleWeekday = (days: number[], day: number): number[] =>
  normalizeWeekdays(days.includes(day) ? days.filter((d) => d !== day) : [...days, day]);

/** "Weekends", "Weekdays", "Every day", or "Fri, Sat". */
export const weekdaysSummary = (days: number[]): string => {
  const d = normalizeWeekdays(days);
  if (d.length === 0) return 'No days';
  if (d.length === 7) return 'Every day';
  if (same(d, WEEKENDS)) return 'Weekends';
  if (same(d, WEEKDAYS_MON_FRI)) return 'Weekdays';
  return d.map((x) => WEEKDAYS_SHORT[x]).join(', ');
};

/** A rule's start-time window: "All day", "From 5:00 PM", "5:00 PM – 10:00 PM". */
export const priceRuleWindow = (startMinute?: number | null, endMinute?: number | null): string => {
  const start = startMinute ?? 0;
  if (start === 0 && (endMinute === null || endMinute === undefined)) return 'All day';
  if (endMinute === null || endMinute === undefined) return `From ${formatMinute(start)}`;
  return `${formatMinute(start)} – ${formatMinute(endMinute)}`;
};

export const PRICE_RULE_FEE_TYPE_LABELS: Record<AmenityPriceRuleFeeType, string> = {
  flat: 'a booking',
  hourly: 'an hour',
  per_night: 'a night',
};

export const priceRuleSummary = (rule: {
  weekdays: number[];
  start_minute?: number | null;
  end_minute?: number | null;
  fee_type: AmenityPriceRuleFeeType;
  amount: string;
}): string =>
  `${weekdaysSummary(rule.weekdays)} · ${priceRuleWindow(rule.start_minute, rule.end_minute)} · ${formatMoney(
    rule.amount
  )} ${PRICE_RULE_FEE_TYPE_LABELS[rule.fee_type]}`;

/** A rule's form is sound: a name, a day, a money amount, and a window that ends after it starts. */
export const priceRuleProblem = (rule: {
  name: string;
  weekdays: number[];
  amount: string;
  start_minute: number;
  end_minute: number | null;
}): string | null => {
  if (!rule.name.trim()) return 'Give the rule a name.';
  if (normalizeWeekdays(rule.weekdays).length === 0) return 'Pick at least one day.';
  if (!isValidFee(rule.amount)) return 'Enter an amount like 200.00.';
  if (rule.end_minute !== null && rule.end_minute <= rule.start_minute) return 'The window must end after it starts.';
  return null;
};

// ---------------------------------------------------------------------------
// Inspections
// ---------------------------------------------------------------------------

export const INSPECTION_KIND_LABELS: Record<ReservationInspectionKind, string> = {
  pre: 'Before use',
  post: 'After use',
};

export const INSPECTION_OUTCOME_LABELS: Record<ReservationInspectionOutcome, string> = {
  ok: 'No issues',
  issues: 'Issues found',
};

/** The amenity's checklist as an unticked form — every item starts as fine. */
export const checklistFrom = (items: string[]): ReservationChecklistItem[] =>
  items.map((item) => ({ item, ok: true, note: null }));

/** An inspection with any item not fine records issues. */
export const outcomeOf = (items: ReservationChecklistItem[]): ReservationInspectionOutcome =>
  items.some((i) => !i.ok) ? 'issues' : 'ok';

/** A checklist typed one item a line: trimmed, empty lines and repeats dropped. */
export const parseChecklist = (text: string): string[] =>
  Array.from(new Set(text.split('\n').map((l) => l.trim()).filter(Boolean)));

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

export const DOCUMENT_KIND_LABELS: Record<AmenityDocumentKind, string> = {
  coi: 'Certificate of insurance',
  waiver: 'Signed waiver',
  other: 'Other document',
};

export const DOCUMENT_STATUS_LABELS: Record<ReservationDocumentStatus, string> = {
  submitted: 'Waiting for review',
  accepted: 'Accepted',
  rejected: 'Rejected',
};

// ---------------------------------------------------------------------------
// Waitlist
// ---------------------------------------------------------------------------

export const WAITLIST_STATUS_LABELS: Record<ReservationWaitlistStatus, string> = {
  waiting: 'Waiting',
  offered: 'Offered a slot',
  accepted: 'Booked',
  expired: 'Offer lapsed',
  withdrawn: 'Withdrawn',
  declined: 'Declined',
};

/** Still in the queue — what a manager may withdraw. */
export const waitlistIsLive = (status: string): boolean => status === 'waiting' || status === 'offered';

/**
 * What a live offer costs the household: "$150.00 + $250.00 deposit",
 * "Free", or null when the entry carries no price (not offered).
 */
export const offerPriceText = (e: {
  status: string;
  offered_fee?: string | null;
  offered_deposit?: string | null;
}): string | null => {
  if (e.status !== 'offered' || e.offered_fee == null) return null;
  const zero = BigInt(0);
  const fee = moneyToCents(e.offered_fee) === zero ? 'Free' : formatMoney(e.offered_fee);
  const deposit =
    e.offered_deposit != null && moneyToCents(e.offered_deposit) > zero
      ? ` + ${formatMoney(e.offered_deposit)} deposit`
      : '';
  return `${fee}${deposit}`;
};

// ---------------------------------------------------------------------------
// Check-in window
// ---------------------------------------------------------------------------

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * Now as the association's wall clock, `YYYY-MM-DDTHH:MM:SS` — the same
 * shape as the `*_local` fields, so the two compare as strings. Falls
 * back to the viewer's clock if the zone is unknown to this browser.
 */
export const nowLocalIn = (timeZone: string | null | undefined, now: Date = new Date()): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timeZone || undefined,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(now);
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00';
    return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}`;
  } catch {
    return (
      `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}` +
      `T${pad2(now.getHours())}:${pad2(now.getMinutes())}:${pad2(now.getSeconds())}`
    );
  }
};

/** `2026-10-08T09:00` → `2026-10-08T09:00:00`, so strings compare. */
const toSeconds = (local: string): string => (local.length === 16 ? `${local}:00` : local.slice(0, 19));

/**
 * Where a booking stands against its check-in window, as the server
 * reports it on staff reads (`check_in_opens_at_local` /
 * `check_in_closes_at_local`, null when check-in does not apply).
 * `unknown` — no window reported — leaves the decision to the server.
 */
export type CheckInState = 'open' | 'not_yet' | 'closed' | 'unknown';

export const checkInState = (
  r: { check_in_opens_at_local?: string | null; check_in_closes_at_local?: string | null },
  nowLocal: string
): CheckInState => {
  if (!r.check_in_opens_at_local || !r.check_in_closes_at_local) return 'unknown';
  const now = toSeconds(nowLocal);
  if (now < toSeconds(r.check_in_opens_at_local)) return 'not_yet';
  if (now >= toSeconds(r.check_in_closes_at_local)) return 'closed';
  return 'open';
};

// ---------------------------------------------------------------------------
// Amenity groups
// ---------------------------------------------------------------------------

/** "1 a day · 3 a week", or "No shared limits". */
export const groupLimitsSummary = (g: {
  per_home_daily_limit?: number | null;
  per_home_weekly_limit?: number | null;
  per_home_monthly_limit?: number | null;
  per_home_active_limit?: number | null;
}): string => {
  const bits: string[] = [];
  if (g.per_home_daily_limit != null) bits.push(`${g.per_home_daily_limit} a day`);
  if (g.per_home_weekly_limit != null) bits.push(`${g.per_home_weekly_limit} a week`);
  if (g.per_home_monthly_limit != null) bits.push(`${g.per_home_monthly_limit} a month`);
  if (g.per_home_active_limit != null) bits.push(`${g.per_home_active_limit} upcoming`);
  return bits.length ? `per household: ${bits.join(' · ')}` : 'No shared limits';
};

/**
 * Amenities for the calendar's day view: grouped ones side by side (a
 * group sits where its first member would), each group in its booking
 * order, ungrouped ones as they were.
 */
export const orderByGroup = <
  T extends { id: string; group_id?: string | null; sort_order: number; name: string }
>(
  list: T[]
): T[] => {
  const out: T[] = [];
  const placed = new Set<string>();
  for (const a of list) {
    if (placed.has(a.id)) continue;
    if (!a.group_id) {
      out.push(a);
      placed.add(a.id);
      continue;
    }
    const members = list
      .filter((b) => b.group_id === a.group_id)
      .sort((x, y) => x.sort_order - y.sort_order || x.name.localeCompare(y.name));
    for (const m of members) {
      out.push(m);
      placed.add(m.id);
    }
  }
  return out;
};

// ---------------------------------------------------------------------------
// Access codes
// ---------------------------------------------------------------------------

export const ACCESS_MODE_LABELS: Record<AmenityAccessCodeMode, string> = {
  none: 'No code',
  static: 'One code for the amenity',
  per_booking: 'A new code for every booking',
};

// ---------------------------------------------------------------------------
// Suspensions
// ---------------------------------------------------------------------------

export type SuspensionState = 'active' | 'scheduled' | 'ended' | 'lifted';

/** Where a suspension stands on `today` (the association's date, YYYY-MM-DD); its days are inclusive. */
export const suspensionState = (
  s: Pick<AmenitySuspensionReturn, 'starts_on' | 'ends_on' | 'lifted_at'>,
  today: string
): SuspensionState => {
  if (s.lifted_at) return 'lifted';
  if (today < s.starts_on) return 'scheduled';
  if (today > s.ends_on) return 'ended';
  return 'active';
};

export const SUSPENSION_STATE_LABELS: Record<SuspensionState, string> = {
  active: 'In force',
  scheduled: 'Starts later',
  ended: 'Ended',
  lifted: 'Lifted',
};

/** What it bars: one amenity or all of them. */
export const suspensionScope = (s: Pick<AmenitySuspensionReturn, 'amenity_name' | 'amenity_id'>): string =>
  s.amenity_id ? s.amenity_name ?? 'One amenity' : 'All amenities';

/** A manual suspension's form: a home, an end on or after the start, a reason. */
export const suspensionProblem = (form: {
  homeId: string;
  startsOn: string;
  endsOn: string;
  reason: string;
}): string | null => {
  if (!form.homeId) return 'Pick a home.';
  if (!form.endsOn) return 'Pick the last day.';
  if (form.startsOn && form.endsOn < form.startsOn) return 'The last day must be on or after the first.';
  if (!form.reason.trim()) return 'Give a reason — the household is told it.';
  return null;
};

// ---------------------------------------------------------------------------
// Revenue report
// ---------------------------------------------------------------------------

export type RevenueField = Exclude<keyof ReservationRevenueMonth, 'month'>;

export const REVENUE_FIELDS: RevenueField[] = [
  'fees_charged',
  'fees_reversed',
  'late_cancel_fees',
  'net_fee_revenue',
  'deposits_charged',
  'deposits_returned',
  'deposits_refund_due',
  'deposit_deductions',
];

export const REVENUE_LABELS: Record<RevenueField, string> = {
  fees_charged: 'Fees charged',
  fees_reversed: 'Reversed',
  late_cancel_fees: 'Late fees',
  net_fee_revenue: 'Net fees',
  deposits_charged: 'Deposits charged',
  deposits_returned: 'Returned',
  deposits_refund_due: 'Owed back',
  deposit_deductions: 'Kept',
};

/** Months summed field by field, exactly. */
export const sumRevenue = (months: ReservationRevenueMonth[], label = 'total'): ReservationRevenueMonth => {
  const out = { month: label } as ReservationRevenueMonth;
  for (const f of REVENUE_FIELDS) {
    out[f] = centsToMoney(months.reduce((acc, m) => acc + moneyToCents(m[f]), BigInt(0)));
  }
  return out;
};

/**
 * Deposits still held at the end of the range: charged, less what was
 * returned, owed back or kept. What the liability account should carry
 * for these bookings.
 */
export const depositsStillHeld = (m: ReservationRevenueMonth): string =>
  centsToMoney(
    moneyToCents(m.deposits_charged) -
      moneyToCents(m.deposits_returned) -
      moneyToCents(m.deposits_refund_due) -
      moneyToCents(m.deposit_deductions)
  );

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-09" → "Sep 2026"; anything else as it came. */
export const formatMonth = (month: string): string => {
  const m = /^(\d{4})-(\d{2})/.exec(month);
  if (!m) return month;
  const idx = Number(m[2]) - 1;
  return idx >= 0 && idx < 12 ? `${MONTHS_SHORT[idx]} ${m[1]}` : month;
};
